import { Suspense, lazy, useState } from "react";
import { Route, Routes } from "react-router";
import { Alert, Box, Button, Card, CardContent, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import { PageHeader } from "@despejo/ui";
import { useAuth } from "./auth/AuthProvider.js";
import { EnrollMfaPage, LoginPage, VerifyMfaPage } from "./auth/AuthPages.js";
import { AppShell } from "./layout/AppShell.js";
import { useCreateOrg, useOrgs } from "./lib/queries.js";
import { supabaseConfigured } from "./lib/supabase.js";
import { DashboardPage } from "./pages/Dashboard.js";
import { TaxpayersPage } from "./pages/Taxpayers.js";

// Route-level code splitting (DES-107): the dashboard and taxpayer list load
// with the app; the heavy screens (engines, playbooks, admin) load on demand.
const TaxpayerWorkspace = lazy(() => import("./pages/TaxpayerWorkspace.js").then((m) => ({ default: m.TaxpayerWorkspace })));
const CalendarPage = lazy(() => import("./pages/Calendar.js").then((m) => ({ default: m.CalendarPage })));
const GuidancePage = lazy(() => import("./pages/Guidance.js").then((m) => ({ default: m.GuidancePage })));
const SettingsPage = lazy(() => import("./pages/Settings.js").then((m) => ({ default: m.SettingsPage })));
const DesignPage = lazy(() => import("./pages/DesignPage.js").then((m) => ({ default: m.DesignPage })));
const AdminHealthPage = lazy(() => import("./pages/AdminHealth.js").then((m) => ({ default: m.AdminHealthPage })));
const LegalTriagePage = lazy(() => import("./pages/LegalTriage.js").then((m) => ({ default: m.LegalTriagePage })));

function Centered({ children }: { children: React.ReactNode }) {
  return <Box sx={{ minHeight: "100dvh", display: "grid", placeItems: "center", p: 2 }}>{children}</Box>;
}

function CreateOrganization() {
  const create = useCreateOrg();
  const [name, setName] = useState("Mis contribuyentes");
  return (
    <Box sx={{ maxWidth: 520 }}>
      <PageHeader title="Bienvenido a Despejo" subtitle="Cree su espacio de trabajo. Ahí vivirán sus contribuyentes, calendario y documentos." />
      <Card>
        <CardContent>
          <Stack spacing={2}>
            <TextField label="Nombre del espacio" value={name} onChange={(e) => setName(e.target.value)} />
            {create.error && <Alert severity="error">{(create.error as Error).message}</Alert>}
            <Button variant="contained" disabled={!name.trim() || create.isPending} onClick={() => create.mutate(name.trim())} sx={{ alignSelf: "flex-start" }}>
              Crear
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

function Workspace({ userId }: { userId: string }) {
  const orgs = useOrgs();
  if (orgs.isLoading) return <CircularProgress aria-label="Cargando" />;
  if (orgs.error) return <Alert severity="error">{(orgs.error as Error).message}</Alert>;
  const org = orgs.data?.[0]?.id as string | undefined;
  if (!org) return <CreateOrganization />;
  return (
    <Suspense fallback={<CircularProgress aria-label="Cargando" sx={{ m: 4 }} />}>
      <Routes>
        <Route path="/" element={<DashboardPage org={org} />} />
        <Route path="/contribuyentes" element={<TaxpayersPage org={org} />} />
        <Route path="/contribuyentes/:id" element={<TaxpayerWorkspace org={org} />} />
        <Route path="/calendario" element={<CalendarPage org={org} />} />
        <Route path="/orientacion" element={<GuidancePage org={org} />} />
        <Route path="/ajustes" element={<SettingsPage userId={userId} />} />
        <Route path="/design" element={<DesignPage />} />
        <Route path="/salud" element={<AdminHealthPage />} />
        <Route path="/legal" element={<LegalTriagePage />} />
        <Route path="*" element={<Typography>Página no encontrada.</Typography>} />
      </Routes>
    </Suspense>
  );
}

export function App() {
  const { state } = useAuth();
  if (!supabaseConfigured) {
    return (
      <Centered>
        <Alert severity="error">Falta configurar VITE_SUPABASE_URL y VITE_SUPABASE_PUBLISHABLE_KEY.</Alert>
      </Centered>
    );
  }
  switch (state.status) {
    case "loading":
      return (
        <Centered>
          <CircularProgress aria-label="Cargando" />
        </Centered>
      );
    case "signed_out":
      return <LoginPage />;
    case "needs_enroll":
      return <EnrollMfaPage />;
    case "needs_verify":
      return <VerifyMfaPage factorId={state.factorId} />;
    case "ready":
      return (
        <AppShell>
          <Workspace userId={state.session.user.id} />
        </AppShell>
      );
  }
}
